// src/pages/customer-panel/documents/DocumentsPage.js
// Customer Panel - Document Management (Admin: paginated user summary, User: dual upload_source tables)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';
import { useDebouncedCallback } from 'use-debounce';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, IconButton, Chip, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Tooltip, Switch, LinearProgress, Snackbar,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import PaginationBar from '../components/PaginationBar';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import TiptapEditor from '../../../components/Common/TiptapEditor';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';
const GREEN_DARK = '#059669';
const GREEN_LIGHT = '#d1fae5';

export default function DocumentsPage() {
    const navigate = useNavigate();
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.active_team?.user_role_in_team || 'customer';
    const isAdmin = userRole === 'admin' || userRole === 'employee';

    // Common
    const [driveConfigured, setDriveConfigured] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // --- Admin state ---
    const [userSummaries, setUserSummaries] = useState([]);
    const [adminPage, setAdminPage] = useState(1);
    const [adminRowsPerPage, setAdminRowsPerPage] = useState(10);
    const [adminTotal, setAdminTotal] = useState(0);
    const [adminSearchInput, setAdminSearchInput] = useState('');
    const [adminSearchQuery, setAdminSearchQuery] = useState('');
    const [adminRoleFilter, setAdminRoleFilter] = useState('all');

    // --- User state: "Files You Uploaded" ---
    const [userDocs, setUserDocs] = useState([]);
    const [userPage, setUserPage] = useState(1);
    const [userRowsPerPage, setUserRowsPerPage] = useState(10);
    const [userTotal, setUserTotal] = useState(0);
    const [userSearchInput, setUserSearchInput] = useState('');
    const [userSearch, setUserSearch] = useState('');
    const [userStatus, setUserStatus] = useState('all');

    // --- User state: "Files Added for You" ---
    const [opDocs, setOpDocs] = useState([]);
    const [opPage, setOpPage] = useState(1);
    const [opRowsPerPage, setOpRowsPerPage] = useState(10);
    const [opTotal, setOpTotal] = useState(0);
    const [opSearchInput, setOpSearchInput] = useState('');
    const [opSearch, setOpSearch] = useState('');
    const [opStatus, setOpStatus] = useState('all');

    // Upload
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [dragging, setDragging] = useState(false);
    const fileInputRef = useRef(null);

    // Bulk selection
    const [selectedDocIds, setSelectedDocIds] = useState(new Set());
    const [bulkProcessing, setBulkProcessing] = useState(false);

    // Edit dialog
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingDoc, setEditingDoc] = useState(null);
    const [editContent, setEditContent] = useState('');
    const [editSaving, setEditSaving] = useState(false);
    const [editLoading, setEditLoading] = useState(false);

    // Tiptap editor
    const editEditor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Link.configure({ openOnClick: false, autolink: true }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Color,
            TextStyle,
            Highlight.configure({ multicolor: true }),
        ],
        content: '',
    });

    // Check drive config
    useEffect(() => {
        axiosInstance.get('/settings/drive')
            .then(res => setDriveConfigured(!!res.data?.selected_drive))
            .catch(() => setDriveConfigured(false));
    }, []);

    // Debounced search callbacks
    const debouncedAdminSearch = useDebouncedCallback((val) => { setAdminSearchQuery(val); setAdminPage(1); }, 500);
    const debouncedUserSearch = useDebouncedCallback((val) => { setUserSearch(val); setUserPage(1); }, 500);
    const debouncedOpSearch = useDebouncedCallback((val) => { setOpSearch(val); setOpPage(1); }, 500);

    // --- Admin fetch ---
    const fetchAdminData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/users/documents/summary', {
                params: {
                    page: adminPage, limit: adminRowsPerPage,
                    search: adminSearchQuery,
                    ...(adminRoleFilter !== 'all' && { role: adminRoleFilter }),
                },
            });
            setUserSummaries(res.data?.users || []);
            setAdminTotal(res.data?.total || 0);
        } catch (err) {
            console.error('Error fetching user summaries:', err);
            setError('Failed to load user documents.');
        } finally {
            setLoading(false);
        }
    }, [adminPage, adminRowsPerPage, adminSearchQuery, adminRoleFilter]);

    // --- User fetch ---
    const fetchUserData = useCallback(async () => {
        setLoading(true);
        try {
            const [userRes, opRes] = await Promise.all([
                axiosInstance.get('/documents', {
                    params: { upload_source: 'user', page: userPage, limit: userRowsPerPage, search: userSearch, status: userStatus },
                }),
                axiosInstance.get('/documents', {
                    params: { upload_source: 'operational', page: opPage, limit: opRowsPerPage, search: opSearch, status: opStatus },
                }),
            ]);
            setUserDocs(userRes.data?.files || []);
            setUserTotal(userRes.data?.total || 0);
            setOpDocs(opRes.data?.files || []);
            setOpTotal(opRes.data?.total || 0);
        } catch (err) {
            console.error('Error fetching documents:', err);
            setError('Failed to load documents.');
        } finally {
            setLoading(false);
        }
    }, [userPage, userRowsPerPage, userSearch, userStatus, opPage, opRowsPerPage, opSearch, opStatus]);

    useEffect(() => {
        if (isAdmin) fetchAdminData();
        else fetchUserData();
    }, [isAdmin, fetchAdminData, fetchUserData]);

    // --- Upload ---
    const handleFileUpload = async (files) => {
        if (!files || files.length === 0) return;
        setUploading(true); setUploadProgress(0); setError(''); setSuccessMsg('');
        try {
            for (let i = 0; i < files.length; i++) {
                const formData = new FormData();
                formData.append('file', files[i]);
                setUploadProgress(Math.round((i / files.length) * 90));
                await axiosInstance.post('/documents/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (p) => {
                        const fileProgress = p.loaded / p.total;
                        setUploadProgress(Math.round(((i + fileProgress) / files.length) * 90));
                    },
                });
            }
            setSuccessMsg(`Successfully uploaded ${files.length} file${files.length > 1 ? 's' : ''}.`);
            fetchUserData();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to upload document.');
        } finally {
            setUploading(false); setUploadProgress(0);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDrop = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); handleFileUpload(e.dataTransfer?.files); }, []);
    const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); }, []);
    const handleDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); }, []);

    // --- Training ---
    const handleToggleTraining = async (doc) => {
        const newStatus = !doc.isTrained;
        try {
            await axiosInstance.post('/documents/training-status', { file_ids: [doc.id], is_trained: newStatus });
            const updater = (list) => list.map((d) => d.id === doc.id ? { ...d, isTrained: newStatus } : d);
            setUserDocs(updater); setOpDocs(updater);
        } catch (err) {
            setError('Failed to update training status.');
        }
    };

    const handleBulkTrain = async (trainStatus) => {
        if (selectedDocIds.size === 0) return;
        setBulkProcessing(true);
        try {
            const ids = Array.from(selectedDocIds);
            await axiosInstance.post('/documents/training-status', { file_ids: ids, is_trained: trainStatus });
            const updater = (list) => list.map((d) => selectedDocIds.has(d.id) ? { ...d, isTrained: trainStatus } : d);
            setUserDocs(updater); setOpDocs(updater);
            setSnackbar({ open: true, message: `${ids.length} document(s) marked as ${trainStatus ? 'trained' : 'untrained'}.`, severity: 'success' });
            setSelectedDocIds(new Set());
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to update documents.', severity: 'error' });
        } finally { setBulkProcessing(false); }
    };

    // --- Selection ---
    const handleSelectDoc = (doc) => {
        setSelectedDocIds((prev) => { const next = new Set(prev); next.has(doc.id) ? next.delete(doc.id) : next.add(doc.id); return next; });
    };
    const handleSelectAll = (docs) => {
        const ids = docs.map(d => d.id);
        const allSelected = ids.every(id => selectedDocIds.has(id));
        setSelectedDocIds((prev) => { const next = new Set(prev); ids.forEach(id => allSelected ? next.delete(id) : next.add(id)); return next; });
    };

    // --- Edit ---
    const handleEditOpen = async (doc) => {
        setEditingDoc(doc); setEditDialogOpen(true); setEditLoading(true);
        if (editEditor) editEditor.commands.setContent('');
        try {
            const res = await axiosInstance.get(`/documents/${doc.id}/content`);
            const content = res.data?.document?.page_content || '';
            setEditContent(content);
            if (editEditor) editEditor.commands.setContent(content);
        } catch {
            setEditContent('');
            if (editEditor) editEditor.commands.setContent('');
        }
        finally { setEditLoading(false); }
    };
    const handleEditSave = async () => {
        if (!editingDoc) return;
        setEditSaving(true);
        try {
            const content = editEditor ? editEditor.getHTML() : editContent;
            await axiosInstance.put(`/documents/${editingDoc.id}/content`, { page_content: content });
            setSnackbar({ open: true, message: 'Document content updated.', severity: 'success' });
            setEditDialogOpen(false); setEditingDoc(null);
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to save.', severity: 'error' });
        } finally { setEditSaving(false); }
    };

    // --- Download ---
    const handleDownload = async (doc) => {
        try {
            const res = await axiosInstance.get(`/documents/download/${doc.id}`);
            if (res.data?.download_url) window.open(res.data.download_url, '_blank');
            else setSnackbar({ open: true, message: 'No download link available.', severity: 'error' });
        } catch { setSnackbar({ open: true, message: 'Failed to download.', severity: 'error' }); }
    };

    // --- Helpers ---
    const formatDate = (dateStr) => {
        if (!dateStr) return '--';
        try { return new Date(dateStr).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }); }
        catch { return '--'; }
    };

    const getFileTypeChip = (type) => {
        const t = (type || '').toLowerCase();
        let label = type || 'File', color = '#64748b', bg = '#f1f5f9';
        if (t.includes('pdf')) { label = 'PDF'; color = '#dc2626'; bg = '#fef2f2'; }
        else if (t.includes('doc') || t.includes('word')) { label = 'DOC'; color = '#2563eb'; bg = '#eff6ff'; }
        else if (t.includes('xls') || t.includes('sheet')) { label = 'XLS'; color = '#16a34a'; bg = '#f0fdf4'; }
        else if (t.includes('csv')) { label = 'CSV'; color = '#059669'; bg = '#ecfdf5'; }
        else if (t.includes('txt') || t.includes('text')) { label = 'TXT'; color = '#64748b'; bg = '#f8fafc'; }
        return <Chip label={label} size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', color, bgcolor: bg, height: 22 }} />;
    };

    // ======================== ADMIN VIEW ========================
    const renderAdminView = () => {
        const adminTotalPages = Math.ceil(adminTotal / adminRowsPerPage) || 1;
        return (
            <Box>
                {/* Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                        User Documents Overview
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <TextField size="small" placeholder="Search by name or email..."
                            value={adminSearchInput}
                            onChange={(e) => { setAdminSearchInput(e.target.value); debouncedAdminSearch(e.target.value); }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment> }}
                            sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { fontFamily, fontSize: '0.85rem', borderRadius: '10px', bgcolor: '#fff', '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: GREEN_ACCENT }, '&.Mui-focused fieldset': { borderColor: GREEN_ACCENT } } }}
                        />
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <Select value={adminRoleFilter}
                                onChange={(e) => { setAdminRoleFilter(e.target.value); setAdminPage(1); }}
                                sx={{ fontFamily, fontSize: '0.85rem', borderRadius: '10px', bgcolor: '#fff' }}>
                                <MenuItem value="all" sx={{ fontFamily, fontSize: '0.85rem' }}>All Roles</MenuItem>
                                <MenuItem value="customer" sx={{ fontFamily, fontSize: '0.85rem' }}>Customer</MenuItem>
                                <MenuItem value="employee" sx={{ fontFamily, fontSize: '0.85rem' }}>Employee</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Table */}
                {userSummaries.length === 0 && !loading ? (
                    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '12px', bgcolor: '#f8fafc' }}>
                        <FolderIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No user documents found.</Typography>
                    </Paper>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['User', 'Role', 'Documents', 'Last Updated', 'Actions'].map((h) => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {userSummaries.map((u) => (
                                    <TableRow key={u.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Box sx={{ p: 0.8, borderRadius: '8px', bgcolor: GREEN_LIGHT, display: 'flex' }}>
                                                    <PersonIcon sx={{ color: GREEN_ACCENT, fontSize: 20 }} />
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>{u.name || 'Unknown'}</Typography>
                                                    {u.email && <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>{u.email}</Typography>}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={u.role || 'user'} size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 22, textTransform: 'capitalize', bgcolor: u.role === 'employee' ? '#ede9fe' : '#f0fdf4', color: u.role === 'employee' ? '#7c3aed' : GREEN_DARK }} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{u.documentCount || 0}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>{formatDate(u.lastUpdated)}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Button size="small" startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                                                onClick={() => navigate(`/customer-panel/documents/${u.id}`)}
                                                sx={{ fontFamily, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', color: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_LIGHT } }}>
                                                View Files
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <PaginationBar
                    currentPage={adminPage} totalPages={adminTotalPages} totalRows={adminTotal}
                    rowsPerPage={adminRowsPerPage} onPageChange={setAdminPage}
                    onRowsPerPageChange={(v) => { setAdminRowsPerPage(v); setAdminPage(1); }}
                />
            </Box>
        );
    };

    // ======================== DOCUMENT TABLE (reusable) ========================
    const DocumentTable = ({ docs, title, icon, iconColor, showUpload, showBulk,
        searchInput, onSearchInput, onSearchDebounced, statusFilter, onStatusChange,
        page, totalRows, rowsPerPage, onPageChange, onRowsPerPageChange }) => {
        const totalPages = Math.ceil(totalRows / rowsPerPage) || 1;
        const tableDocIds = docs.map(d => d.id);
        const allSelected = docs.length > 0 && tableDocIds.every(id => selectedDocIds.has(id));
        const someSelected = tableDocIds.some(id => selectedDocIds.has(id)) && !allSelected;
        const selectedInTable = docs.filter(d => selectedDocIds.has(d.id));

        return (
            <Box sx={{ mb: 4 }}>
                {/* Section header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {icon}
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>{title}</Typography>
                        <Chip label={totalRows} size="small" sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_LIGHT, color: GREEN_DARK, height: 22, fontSize: '0.72rem' }} />
                    </Box>
                </Box>

                {/* Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <TextField size="small" placeholder="Search files..." value={searchInput}
                            onChange={(e) => { onSearchInput(e.target.value); onSearchDebounced(e.target.value); }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment> }}
                            sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { fontFamily, fontSize: '0.85rem', borderRadius: '10px', bgcolor: '#fff', '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: GREEN_ACCENT }, '&.Mui-focused fieldset': { borderColor: GREEN_ACCENT } } }}
                        />
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <Select value={statusFilter} onChange={(e) => onStatusChange(e.target.value)}
                                sx={{ fontFamily, fontSize: '0.85rem', borderRadius: '10px', bgcolor: '#fff' }}>
                                <MenuItem value="all" sx={{ fontFamily, fontSize: '0.85rem' }}>All Status</MenuItem>
                                <MenuItem value="trained" sx={{ fontFamily, fontSize: '0.85rem' }}>Trained</MenuItem>
                                <MenuItem value="untrained" sx={{ fontFamily, fontSize: '0.85rem' }}>Untrained</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Bulk toolbar */}
                {showBulk && selectedInTable.length > 0 && (
                    <Paper elevation={0} sx={{ mb: 1.5, p: 1.5, px: 2, borderRadius: '12px', bgcolor: '#f0fdf4', border: `1px solid ${GREEN_ACCENT}40`, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontFamily, fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}>{selectedInTable.length} selected</Typography>
                        <Button size="small" variant="contained" disabled={bulkProcessing} onClick={() => handleBulkTrain(true)}
                            startIcon={bulkProcessing ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', bgcolor: GREEN_ACCENT, borderRadius: '8px', '&:hover': { bgcolor: GREEN_DARK } }}>
                            Mark Trained
                        </Button>
                        <Button size="small" variant="contained" disabled={bulkProcessing} onClick={() => handleBulkTrain(false)}
                            startIcon={bulkProcessing ? <CircularProgress size={14} color="inherit" /> : <PendingIcon sx={{ fontSize: 16 }} />}
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', bgcolor: '#f59e0b', borderRadius: '8px', '&:hover': { bgcolor: '#d97706' } }}>
                            Mark Untrained
                        </Button>
                        <Typography onClick={() => setSelectedDocIds(new Set())} sx={{ fontFamily, fontSize: '0.78rem', color: '#64748b', cursor: 'pointer', ml: 'auto', '&:hover': { color: '#1e293b', textDecoration: 'underline' } }}>
                            Clear
                        </Typography>
                    </Paper>
                )}

                {/* Table */}
                {docs.length === 0 ? (
                    <Paper sx={{ p: 3, textAlign: 'center', borderRadius: '12px', bgcolor: '#f8fafc' }}>
                        <InsertDriveFileIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 0.5 }} />
                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No documents found.</Typography>
                    </Paper>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell padding="checkbox" sx={{ borderBottom: '2px solid #e2e8f0' }}>
                                        <Checkbox size="small" checked={allSelected} indeterminate={someSelected} onChange={() => handleSelectAll(docs)}
                                            sx={{ color: '#94a3b8', '&.Mui-checked': { color: GREEN_ACCENT }, '&.MuiCheckbox-indeterminate': { color: GREEN_ACCENT } }} />
                                    </TableCell>
                                    {['Name', 'Type', 'Status', 'Uploaded', 'Actions'].map((h) => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, color: '#475569', fontSize: '0.8rem', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {docs.map((doc, idx) => {
                                    const isSelected = selectedDocIds.has(doc.id);
                                    return (
                                        <TableRow key={doc.id || idx} selected={isSelected} sx={{ '&:hover': { bgcolor: '#f8fafc' }, ...(isSelected && { bgcolor: '#f0fdf4' }) }}>
                                            <TableCell padding="checkbox">
                                                <Checkbox size="small" checked={isSelected} onChange={() => handleSelectDoc(doc)}
                                                    sx={{ color: '#94a3b8', '&.Mui-checked': { color: GREEN_ACCENT } }} />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <DescriptionIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 500, color: '#1e293b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {doc.filename || 'Untitled'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>{getFileTypeChip(doc.fileType || '')}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {doc.isTrained ? (
                                                        <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label="Trained" size="small"
                                                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: GREEN_LIGHT, color: GREEN_DARK, height: 24, '& .MuiChip-icon': { color: GREEN_DARK } }} />
                                                    ) : (
                                                        <Chip icon={<PendingIcon sx={{ fontSize: 14 }} />} label="Untrained" size="small"
                                                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#fef3c7', color: '#92400e', height: 24, '& .MuiChip-icon': { color: '#92400e' } }} />
                                                    )}
                                                    <Tooltip title={doc.isTrained ? 'Disable training' : 'Enable training'}>
                                                        <Switch size="small" checked={!!doc.isTrained} onChange={() => handleToggleTraining(doc)}
                                                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: GREEN_ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: GREEN_ACCENT } }} />
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                                    {formatDate(doc.upload_date)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Tooltip title="Edit content">
                                                        <IconButton size="small" onClick={() => handleEditOpen(doc)} sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {doc.webViewLink && (
                                                        <Tooltip title="View in Drive">
                                                            <IconButton size="small" component="a" href={doc.webViewLink} target="_blank" rel="noopener noreferrer" sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}>
                                                                <OpenInNewIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Download">
                                                        <IconButton size="small" onClick={() => handleDownload(doc)} sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}>
                                                            <DownloadIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <PaginationBar
                    currentPage={page} totalPages={totalPages} totalRows={totalRows}
                    rowsPerPage={rowsPerPage} onPageChange={onPageChange}
                    onRowsPerPageChange={(v) => { onRowsPerPageChange(v); onPageChange(1); }}
                />
            </Box>
        );
    };

    // ======================== USER VIEW ========================
    const renderUserView = () => (
        <Box>
            {/* Upload Area */}
            <Paper elevation={0} onDrop={uploading ? undefined : handleDrop} onDragOver={uploading ? undefined : handleDragOver} onDragLeave={uploading ? undefined : handleDragLeave}
                sx={{ mb: 3, p: 4, border: dragging ? `2px dashed ${GREEN_ACCENT}` : '2px dashed #e2e8f0', borderRadius: '14px', textAlign: 'center',
                    bgcolor: dragging ? GREEN_LIGHT : '#fafbfc', cursor: uploading ? 'default' : 'pointer', transition: 'all 0.2s ease',
                    opacity: uploading ? 0.7 : 1, '&:hover': uploading ? {} : { borderColor: GREEN_ACCENT, bgcolor: '#f0fdf4' } }}
                onClick={() => !uploading && fileInputRef.current?.click()}>
                <input type="file" ref={fileInputRef} onChange={(e) => handleFileUpload(e.target.files)} multiple hidden
                    accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.png,.jpg,.jpeg" />
                <CloudUploadIcon sx={{ fontSize: 48, color: dragging ? GREEN_ACCENT : '#94a3b8', mb: 1 }} />
                <Typography sx={{ fontFamily, fontWeight: 600, color: dragging ? GREEN_DARK : '#475569', mb: 0.5 }}>
                    {dragging ? 'Drop files here' : 'Drag & drop files here, or click to browse'}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily, color: '#94a3b8' }}>Supports PDF, DOC, DOCX, TXT, CSV, XLS, XLSX, PNG, JPG</Typography>
                {uploading && (
                    <Box sx={{ mt: 2, px: 4 }}>
                        <LinearProgress variant={uploadProgress >= 90 ? 'indeterminate' : 'determinate'} value={uploadProgress}
                            sx={{ borderRadius: '4px', height: 6, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: GREEN_ACCENT } }} />
                        <Typography variant="caption" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>
                            {uploadProgress >= 90 ? 'Processing on server, please wait...' : `Uploading... ${uploadProgress}%`}
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Section 1: Files Added for You */}
            <DocumentTable
                docs={opDocs} title="Files Added for You" showBulk
                icon={<PersonIcon sx={{ color: '#6366f1', fontSize: 22 }} />} iconColor="#6366f1"
                searchInput={opSearchInput} onSearchInput={setOpSearchInput} onSearchDebounced={debouncedOpSearch}
                statusFilter={opStatus} onStatusChange={(v) => { setOpStatus(v); setOpPage(1); }}
                page={opPage} totalRows={opTotal} rowsPerPage={opRowsPerPage}
                onPageChange={setOpPage} onRowsPerPageChange={setOpRowsPerPage}
            />

            {/* Section 2: Files You Uploaded */}
            <DocumentTable
                docs={userDocs} title="Files You Uploaded" showBulk
                icon={<CloudUploadIcon sx={{ color: GREEN_ACCENT, fontSize: 22 }} />}
                searchInput={userSearchInput} onSearchInput={setUserSearchInput} onSearchDebounced={debouncedUserSearch}
                statusFilter={userStatus} onStatusChange={(v) => { setUserStatus(v); setUserPage(1); }}
                page={userPage} totalRows={userTotal} rowsPerPage={userRowsPerPage}
                onPageChange={setUserPage} onRowsPerPageChange={setUserRowsPerPage}
            />
        </Box>
    );

    // ======================== MAIN RENDER ========================
    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* Header */}
            <Paper elevation={0} sx={{ p: 2, px: 3, mb: 3, borderRadius: '16px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <DescriptionIcon sx={{ fontSize: 28 }} />
                    <Box>
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700 }}>Documents</Typography>
                        <Typography variant="body2" sx={{ fontFamily, opacity: 0.9 }}>{isAdmin ? 'Manage documents across all users' : 'Upload and manage your documents'}</Typography>
                    </Box>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton onClick={isAdmin ? fetchAdminData : fetchUserData} disabled={loading} sx={{ color: '#fff' }}><RefreshIcon /></IconButton>
                </Tooltip>
            </Paper>

            {driveConfigured === null ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: GREEN_ACCENT }} /></Box>
            ) : driveConfigured === false ? (
                <Paper elevation={0} sx={{ p: 4, borderRadius: '16px', border: '1px solid #fbbf24', bgcolor: '#fffbeb', textAlign: 'center' }}>
                    <WarningAmberIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#92400e', mb: 1 }}>Central Drive Setup Required</Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#78350f', mb: 2 }}>
                        A cloud storage drive (Google Drive or OneDrive) must be configured before documents can be managed.
                        {isAdmin ? ' Please go to Settings to connect and configure your cloud storage.' : ' Please contact your administrator to set up cloud storage.'}
                    </Typography>
                    {isAdmin && (
                        <Button variant="contained" onClick={() => navigate('/customer-panel/settings')}
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' }, borderRadius: '10px' }}>
                            Go to Settings
                        </Button>
                    )}
                </Paper>
            ) : (
                <>
                    {error && <Alert severity="error" sx={{ mb: 2, fontFamily, borderRadius: '10px' }} onClose={() => setError('')}>{error}</Alert>}
                    {successMsg && <Alert severity="success" sx={{ mb: 2, fontFamily, borderRadius: '10px' }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: GREEN_ACCENT }} /></Box>
                    ) : isAdmin ? renderAdminView() : renderUserView()}
                </>
            )}

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={() => { setEditDialogOpen(false); setEditingDoc(null); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><EditIcon sx={{ color: GREEN_ACCENT }} />Edit Document Content</Box>
                    <IconButton size="small" onClick={() => { setEditDialogOpen(false); setEditingDoc(null); }}><CloseIcon fontSize="small" /></IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b', mb: 2 }}>
                        Edit content of &quot;{editingDoc?.filename || 'this document'}&quot; for AI training.
                    </Typography>
                    {editLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={32} sx={{ color: GREEN_ACCENT }} /></Box>
                    ) : (
                        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', '&:hover': { borderColor: GREEN_ACCENT }, '&:focus-within': { borderColor: GREEN_ACCENT, boxShadow: `0 0 0 1px ${GREEN_ACCENT}` } }}>
                            <TiptapEditor editor={editEditor} height="350px" />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={() => { setEditDialogOpen(false); setEditingDoc(null); }} disabled={editSaving} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleEditSave} disabled={editSaving || editLoading} variant="contained"
                        startIcon={editSaving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '10px', bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK } }}>
                        {editSaving ? 'Saving...' : 'Save Content'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%', fontFamily, borderRadius: '10px' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}
